import { createTheme, alpha, adaptV4Theme } from "@mui/material/styles"
import createBreakpoints from "@mui/system/createTheme/createBreakpoints"

import {
  teal,
  lightBlue,
  lightGreen,
  orange,
  red,
  blueGrey,
  indigo
} from "./palette"
import {
  FOCUS_COLOR,
  FOCUS_COLOR_ON_DARK,
  focusRing,
  focusRingOnDark,
  focusRingTwoTone
} from "./focusStyles"

const desktopFonts = {
  root: {
    fontSize: "1rem"
  },
  h1: {
    // 34 : 42
    fontSize: "2.125rem !important",
    lineHeight: "2.625rem"
  },
  h2: {
    //  28 : 34
    fontSize: "1.75rem",
    lineHeight: "2.125rem"
  },
  h3: {
    // 24 : 32
    fontSize: "1.5rem",
    lineHeight: "2rem"
  },
  h4: {
    // 18 : 26
    fontSize: "1.125rem",
    lineHeight: "1.625rem"
  },
  h5: {
    // 16 : 23
    fontSize: "1rem",
    lineHeight: "1.4375rem"
  },
  h6: {
    // 14 : 20
    fontSize: "0.875rem",
    lineHeight: "1.25rem"
  },
  subtitle1: {
    // 18 : 24
    fontSize: "1.125rem",
    lineHeight: "1.5rem"
  },
  subtitle2: {
    // 16 : 22
    fontSize: "1rem",
    lineHeight: "1.375rem"
  },
  body1: {
    // 24 : 32
    fontSize: "1.5rem",
    lineHeight: "2rem"
  },
  body2: {
    // 22 : 28
    fontSize: "1.375rem",
    lineHeight: "1.75rem"
  },
  button: {
    fontSize: "0.875rem"
  },
  citation: {
    // 14 : 18
    fontSize: "0.875rem",
    lineHeight: "1.25rem"
  },
  caption: {
    fontSize: "0.75rem"
  },
  overline: {
    fontSize: "0.75rem"
  },
  iconsmall: {
    fontSize: "1rem"
  },
  iconlarge: {
    fontSize: "2.25rem"
  },
  input: {
    fontSize: "0.75rem"
  }
}

const mobileFonts = {
  root: {
    fontSize: "0.875rem",
    fontWeight: 500
  },
  h1: {
    // 24 : 30
    fontSize: "1.5rem",
    fontWeight: 500,
    lineHeight: "1.875rem"
  },
  h2: {
    // 20 : 28
    fontSize: "1.25rem",
    fontWeight: 500,
    lineHeight: "1.75rem"
  },
  h3: {
    // 18 : 24
    fontSize: "1.125rem",
    fontWeight: 500,
    lineHeight: "1.5rem"
  },
  h4: {
    // 16 : 22
    fontSize: "1rem",
    fontWeight: 500,
    lineHeight: "1.375rem"
  },
  h5: {
    // 16 : 21
    fontSize: "1rem",
    fontWeight: 500,
    lineHeight: "1.3125rem"
  },
  h6: {
    // 13 : 16
    fontSize: "0.8125rem",
    fontWeight: 500,
    lineHeight: "1rem"
  },
  subtitle1: {
    // 14 : 20
    fontSize: "0.875rem",
    fontWeight: 400,
    lineHeight: "1.25rem"
  },
  subtitle2: {
    // 12 : 18
    fontSize: "0.75rem",
    fontWeight: 400,
    lineHeight: "1.125rem"
  },
  body1: {
    // 18 : 24
    fontSize: "1.125rem",
    fontWeight: 400,
    lineHeight: "1.5rem"
  },
  body2: {
    // 16 : 20
    fontSize: "1rem",
    fontWeight: 400,
    lineHeight: "1.25rem"
  },
  button: {
    fontSize: "0.8125rem",
    fontWeight: 500
  },
  citation: {
    // 13 : 16
    fontSize: "0.8125rem",
    fontWeight: 400,
    lineHeight: "1rem"
  },
  caption: {
    fontSize: "0.75rem",
    fontWeight: 400
  },
  overline: {
    fontSize: "0.75rem",
    fontWeight: 400
  },
  iconsmall: {
    fontSize: "0.875rem"
  },
  iconlarge: {
    fontSize: "2.25rem"
  },
  input: {
    fontSize: "0.75rem",
    fontWeight: 400
  }
}

// APP Breakpoints
const breakpoints = createBreakpoints({
  values: {
    xs: 0,
    sm: 600,
    md: 960,
    lg: 1280,
    xl: 1920
  }
})

const FONT_FAMILY = [
  "Effra",
  "PT Serif",
  "Rubik",
  "Segoe UI",
  "Tahoma",
  "Geneva",
  "Verdana",
  "sans-serif"
].join(",")

export const StudentTheme = createTheme({
  ...adaptV4Theme({
    breakpoints: breakpoints,
    palette: {
      primary: blueGrey,
      secondary: lightBlue,
      error: red,
      success: lightGreen,
      info: indigo,
      warning: orange,
      grey: teal,
      text: {
        primary: blueGrey[800],
        secondary: lightBlue[900],
        disabled: blueGrey[500], // Change to blueGrey[300]
        muted: blueGrey[500]
      },
      background: {
        default: "white"
      },
      link: {
        main: lightBlue[700],
        hover: lightBlue[800],
        disabled: lightBlue[300]
      },
      status: {
        active: "#FFFFFF",
        inactive: blueGrey[100]
      },
      focus: FOCUS_COLOR,
      focusOnDark: FOCUS_COLOR_ON_DARK,
      mutes: {
        light: blueGrey[50],
        mid: blueGrey[200]
      },
      subjects: {
        act: {
          math: {
            primary: red[600],
            secondary: red[500]
          },
          science: {
            primary: lightGreen[900],
            secondary: lightGreen[700]
          },
          english: {
            primary: indigo[600],
            secondary: indigo[500]
          },
          reading: {
            primary: lightBlue[700],
            secondary: lightBlue[500]
          },
          overall: {
            primary: teal[600],
            secondary: teal[400]
          }
        },
        sat: {
          overall: {
            primary: teal[600],
            secondary: teal[400]
          },
          math: {
            primary: red[600],
            secondary: red[500]
          },
          writingandlanguage: {
            primary: indigo[600],
            secondary: indigo[500]
          },
          reading: {
            primary: lightBlue[700],
            secondary: lightBlue[500]
          }
        },
        psat: {
          overall: {
            primary: teal[600],
            secondary: teal[400]
          },
          math: {
            primary: red[600],
            secondary: red[500]
          },
          writingandlanguage: {
            primary: indigo[600],
            secondary: indigo[500]
          },
          reading: {
            primary: lightBlue[700],
            secondary: lightBlue[500]
          }
        },
        dsat: {
          overall: {
            primary: teal[600],
            secondary: teal[400]
          },
          math: {
            primary: red[600],
            secondary: red[500]
          },
          mathematics: {
            primary: red[600],
            secondary: red[500]
          },
          readingandwriting: {
            primary: lightBlue[700],
            secondary: lightBlue[500]
          }
        },
        staar: {
          overall: {
            primary: teal[600],
            secondary: teal[400]
          },
          algebra: {
            primary: red[600],
            secondary: red[500]
          },
          algebrai: {
            primary: red[600],
            secondary: red[500]
          },
          algebraii: {
            primary: red[600],
            secondary: red[500]
          },
          biology: {
            primary: lightGreen[900],
            secondary: lightGreen[700]
          },
          english: {
            primary: indigo[600],
            secondary: indigo[500]
          },
          englishi: {
            primary: indigo[600],
            secondary: indigo[500]
          },
          englishii: {
            primary: indigo[600],
            secondary: indigo[500]
          },
          englishiii: {
            primary: indigo[600],
            secondary: indigo[500]
          },
          ushistory: {
            primary: lightBlue[700],
            secondary: lightBlue[500]
          }
        },
        tsia2: {
          elar: {
            primary: lightBlue[700],
            secondary: lightBlue[500]
          },
          math: {
            primary: red[600],
            secondary: red[500]
          }
        }
      }
    },
    spacing: [0, 4, 8, 12, 16, 24, 32, 48, 64, 80, 124],
    typography: {
      fontFamily: FONT_FAMILY,
      h1: {
        ...mobileFonts.h1,
        [breakpoints.up("md")]: {
          ...desktopFonts.h1
        }
      },
      h2: {
        ...mobileFonts.h2,
        [breakpoints.up("md")]: {
          ...desktopFonts.h2
        }
      },
      h3: {
        ...mobileFonts.h3,
        [breakpoints.up("md")]: {
          ...desktopFonts.h3
        }
      },
      h4: {
        ...mobileFonts.h4,
        [breakpoints.up("md")]: {
          ...desktopFonts.h4
        }
      },
      h5: {
        ...mobileFonts.h5,
        [breakpoints.up("md")]: {
          ...desktopFonts.h5
        }
      },
      h6: {
        ...mobileFonts.h6,
        [breakpoints.up("md")]: {
          ...desktopFonts.h6
        }
      },
      subtitle1: {
        ...mobileFonts.subtitle1,
        [breakpoints.up("md")]: {
          ...desktopFonts.subtitle1
        }
      },
      subtitle2: {
        ...mobileFonts.subtitle2,
        [breakpoints.up("md")]: {
          ...desktopFonts.subtitle2
        }
      },
      body1: {
        ...mobileFonts.body1,
        [breakpoints.up("md")]: {
          ...desktopFonts.body1
        }
      },
      body2: {
        ...mobileFonts.body2,
        [breakpoints.up("md")]: {
          ...desktopFonts.body2
        }
      },
      button: {
        ...mobileFonts.button,
        textTransform: "none",
        [breakpoints.up("md")]: {
          ...desktopFonts.button
        }
      },
      // @ts-ignore
      citation: {
        fontFamily: FONT_FAMILY,
        ...mobileFonts.citation,
        textTransform: "none",
        [breakpoints.up("md")]: {
          ...desktopFonts.citation
        }
      },
      overline: {
        ...mobileFonts.overline,
        [breakpoints.up("md")]: {
          ...desktopFonts.overline
        }
      },
      caption: {
        ...mobileFonts.caption,
        [breakpoints.up("md")]: {
          ...desktopFonts.caption
        }
      }
    },
    shadows: [
      "none",
      "0px 2px 1px -1px rgba(0,0,0,0.2),0px 1px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0.12)",
      "0 3px 1px -2px rgba(0,0,0,0.20), 0 2px 2px 0 rgba(0,0,0,0.14), 0 1px 5px 0 rgba(0,0,0,0.12)",
      "0px 3px 3px -2px rgba(0,0,0,0.2),0px 3px 4px 0px rgba(0,0,0,0.14),0px 1px 8px 0px rgba(0,0,0,0.12)",
      "0 2px 4px -1px rgba(0,0,0,0.20), 0 4px 5px 0 rgba(0,0,0,0.14), 0 1px 10px 0 rgba(0,0,0,0.12)",
      "0px 3px 5px -1px rgba(0,0,0,0.2),0px 5px 8px 0px rgba(0,0,0,0.14),0px 1px 14px 0px rgba(0,0,0,0.12)",
      "0 3px 5px -1px rgba(0,0,0,0.20), 0 6px 10px 0 rgba(0,0,0,0.14), 0 1px 18px 0 rgba(0,0,0,0.12)",
      "0px 4px 5px -2px rgba(0,0,0,0.2),0px 7px 10px 1px rgba(0,0,0,0.14),0px 2px 16px 1px rgba(0,0,0,0.12)",
      "0 5px 5px -3px rgba(0,0,0,0.20), 0 8px 10px 1px rgba(0,0,0,0.14), 0 3px 14px 2px rgba(0,0,0,0.12)",
      "0px 5px 6px -3px rgba(0,0,0,0.2),0px 9px 12px 1px rgba(0,0,0,0.14),0px 3px 16px 2px rgba(0,0,0,0.12)",
      "0px 6px 6px -3px rgba(0,0,0,0.2),0px 10px 14px 1px rgba(0,0,0,0.14),0px 4px 18px 3px rgba(0,0,0,0.12)",
      "0px 6px 7px -4px rgba(0,0,0,0.2),0px 11px 15px 1px rgba(0,0,0,0.14),0px 4px 20px 3px rgba(0,0,0,0.12)",
      "0px 7px 8px -4px rgba(0,0,0,0.2),0px 12px 17px 2px rgba(0,0,0,0.14),0px 5px 22px 4px rgba(0,0,0,0.12)",
      "0px 7px 8px -4px rgba(0,0,0,0.2),0px 13px 19px 2px rgba(0,0,0,0.14),0px 5px 24px 4px rgba(0,0,0,0.12)",
      "0px 7px 9px -4px rgba(0,0,0,0.2),0px 14px 21px 2px rgba(0,0,0,0.14),0px 5px 26px 4px rgba(0,0,0,0.12)",
      "0px 8px 9px -5px rgba(0,0,0,0.2),0px 15px 22px 2px rgba(0,0,0,0.14),0px 6px 28px 5px rgba(0,0,0,0.12)",
      "0px 8px 10px -5px rgba(0,0,0,0.2),0px 16px 24px 2px rgba(0,0,0,0.14),0px 6px 30px 5px rgba(0,0,0,0.12)",
      "0px 8px 11px -5px rgba(0,0,0,0.2),0px 17px 26px 2px rgba(0,0,0,0.14),0px 6px 32px 5px rgba(0,0,0,0.12)",
      "0px 9px 11px -5px rgba(0,0,0,0.2),0px 18px 28px 2px rgba(0,0,0,0.14),0px 7px 34px 6px rgba(0,0,0,0.12)",
      "0px 9px 12px -6px rgba(0,0,0,0.2),0px 19px 29px 2px rgba(0,0,0,0.14),0px 7px 36px 6px rgba(0,0,0,0.12)",
      "0px 10px 13px -6px rgba(0,0,0,0.2),0px 20px 31px 3px rgba(0,0,0,0.14),0px 8px 38px 7px rgba(0,0,0,0.12)",
      "0px 10px 13px -6px rgba(0,0,0,0.2),0px 21px 33px 3px rgba(0,0,0,0.14),0px 8px 40px 7px rgba(0,0,0,0.12)",
      "0px 10px 14px -6px rgba(0,0,0,0.2),0px 22px 35px 3px rgba(0,0,0,0.14),0px 8px 42px 7px rgba(0,0,0,0.12)",
      "0px 11px 14px -7px rgba(0,0,0,0.2),0px 23px 36px 3px rgba(0,0,0,0.14),0px 9px 44px 8px rgba(0,0,0,0.12)",
      "0px 11px 15px -7px rgba(0,0,0,0.2),0px 24px 38px 3px rgba(0,0,0,0.14),0px 9px 46px 8px rgba(0,0,0,0.12)"
    ],
    overrides: {
      // Fix FontAwesome Icon clipping
      MuiTypography: {
        root: {
          ...mobileFonts.root,
          [breakpoints.up("md")]: {
            ...desktopFonts.root
          }
        }
      },
      MuiIcon: {
        root: {
          overflow: "visible"
        }
      },
      MuiSvgIcon: {
        root: {
          ...mobileFonts.iconsmall,
          [breakpoints.up("md")]: {
            ...desktopFonts.iconsmall
          }
        }
      },
      MuiInputBase: {
        root: {
          ...mobileFonts.input,
          [breakpoints.up("md")]: {
            ...desktopFonts.input
          }
        }
      },
      MuiCssBaseline: {
        styleOverrides: {
          ["button,a,div,p,h1,h2,h3,h4,h5,h6,img,circle,ul,th"]: {
            "&:focus": focusRing.ring,
            "&:focus-visible": focusRing.ring
          },
          ".block_sizes": {
            minHeight: "38vw",
            backgroundColor: lightBlue["50"]
          },
          ".SmallColumn .block_sizes": {
            minHeight: 300
          },
          ".SmallColumn .MuiInputBase-root": {
            ...mobileFonts.input
          },
          ".SmallColumn .MuiTypography-root": {
            ...mobileFonts.root
          },
          ".SmallColumn .MuiTypography-h1": {
            ...mobileFonts.h1
          },
          ".SmallColumn .MuiTypography-h2": {
            ...mobileFonts.h2
          },
          ".SmallColumn .MuiTypography-h3": {
            ...mobileFonts.h3
          },
          ".SmallColumn .MuiTypography-h4": {
            ...mobileFonts.h4
          },
          ".SmallColumn .MuiTypography-h5": {
            ...mobileFonts.h5
          },
          ".SmallColumn .MuiTypography-h6": {
            ...mobileFonts.h6
          },
          ".SmallColumn .MuiTypography-button": {
            ...mobileFonts.button
          },
          ".SmallColumn .MuiTypography-caption": {
            ...mobileFonts.caption
          },
          ".SmallColumn .MuiTypography-overline": {
            ...mobileFonts.overline
          },
          ".SmallColumn .MuiTypography-body1": {
            ...mobileFonts.body1
          },
          ".SmallColumn .MuiTypography-body2": {
            ...mobileFonts.body2
          },
          ".SmallColumn .MuiTypography-subtitle1": {
            ...mobileFonts.subtitle1
          },
          ".SmallColumn .MuiTypography-subtitle2": {
            ...mobileFonts.subtitle2
          },
          ".SmallColumn .MuiIcon-fontSizeLarge": {
            ...mobileFonts.iconlarge
          },
          ".SmallColumn .MuiIcon-fontSizeSmall": {
            ...mobileFonts.iconsmall
          },
          ".SmallColumn .MuiSvgIcon-root": {
            ...mobileFonts.iconsmall
          }
        }
      }
    }
  }),
  mixins: { focusRing, focusRingOnDark, focusRingTwoTone },
  shape: {
    borderRadius: 4, // MUI's original borderRadius size (let's not change without extensive testing)
    // Custom border Radius sizes
    borderRadiusSm: 3,
    borderRadiusMd: 6,
    borderRadiusLg: 12
  },
  components: {
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: alpha(blueGrey[900], 0.65),
          margin: "8px 0px",
          fontSize: "0.75rem",
          "&:first-letter": {
            textTransform: "uppercase"
          }
        },
        tooltipPlacementTop: {
          margin: "1px 0px !important"
        },
        tooltipArrow: {
          backgroundColor: alpha(blueGrey[900], 0.75)
        },
        arrow: {
          color: alpha(blueGrey[900], 0.75)
        }
      }
    },
    MuiTypography: {
      variants: [
        {
          props: { variant: "citation" },
          style: {}
        },
        {
          props: { color: "muted" },
          style: { color: blueGrey[500] }
        },
        {
          props: { color: "link" },
          style: { color: lightBlue[700] }
        }
      ]
    },
    MuiLink: {
      defaultProps: {
        underline: "hover"
      },
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.link.main,
          "&:hover": { color: theme.palette.link.hover },
          "&:focus-visible": { color: theme.palette.link.hover },
          "&[aria-disabled='true']": {
            color: theme.palette.link.disabled,
            pointerEvents: "none"
          }
        })
      }
    }
  }
})
